import React, { useEffect, useState } from "react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { Download } from "lucide-react";
import QRCode from "react-qr-code";
import QRCodeLib from "qrcode";
import CertificateDocument from "./CertificateDocument";

const AnnualPracticingCertificate = ({
    year,
    certificateNumber,
    name,
    licenseNo,
    approvedDate,
    signature
}) => {
    const [qrCodeImage, setQrCodeImage] = useState(null);

    const VERIFY_URL = licenseNo
        ? `https://lsb.go.ke/verify/${licenseNo}`
        : "";

    /* Generate QR image for PDF */
    useEffect(() => {
        const generateQr = async () => {
            if (!VERIFY_URL) return;
            try {
                const img = await QRCodeLib.toDataURL(VERIFY_URL);
                setQrCodeImage(img);
            } catch (err) {
                console.error("QR generation failed", err);
            }
        };
        generateQr();
    }, [VERIFY_URL]);

    return (
        <div>
            {/* Download button */}
            <div className="pb-3 w-[150px]">
                <PDFDownloadLink
                    document={
                        <CertificateDocument
                            year={year}
                            certificateNumber={certificateNumber}
                            name={name}
                            licenseNo={licenseNo}
                            approvedDate={approvedDate}
                            signature={signature}
                            qrCodeImage={qrCodeImage}
                        />
                    }
                    fileName={`Annual_Certificate_${licenseNo}_${year}.pdf`}
                    className="flex items-center gap-2 px-4 py-2 rounded-md bg-amber-800 text-white text-sm font-bold shadow-md hover:bg-amber-900 print:hidden"
                >
                    {({ loading }) =>
                        loading ? "Preparing PDF..." : (
                            <>
                                <Download className="w-5 h-5" />
                                Download
                            </>
                        )
                    }
                </PDFDownloadLink>
            </div>

            {/* Certificate Preview */}
            <div className="max-w-4xl mx-auto p-20 bg-white border border-gray-300 shadow-lg">
                {/* Header */}
                <div className="text-end text-sm mb-2">
                    CERTIFICATE NO:{" "}
                    <strong className="underline">
                        {certificateNumber}
                    </strong>
                </div>

                <div className="text-center mb-4 leading-tight">
                    <img src="/certlogo.png" alt="logo" className="mx-auto w-60 mb-3" />
                    <h6 className="font-medium mt-2">REPUBLIC OF KENYA <br /> LAND SURVEYORS' BOARD</h6>
                    <div className="mt-1 w-72 h-1 bg-black mx-auto" />
                </div>

                <h3 className="text-center font-bold uppercase mb-6">
                    Annual Practicing Certificate
                </h3>

                {/* Main Content */}
                <div className="text-center mb-12">
                    <p>This is to certify that</p>
                    <h1 className="font-bold mt-4">{name}</h1>

                    <p className="mt-4 leading-8">
                        LICENSE NO. <b>{licenseNo}</b> <br />
                        is authorized to practice in the Republic of Kenya as a
                    </p>

                    <h2 className="font-bold uppercase my-4">
                        Licensed Surveyor
                    </h2>

                    <p className="leading-8">
                        for the period from{" "}
                        <strong>
                            {new Date(approvedDate).toLocaleDateString("en-KE", {
                                year: "numeric",
                                month: "long",
                                day: "numeric"
                            })}
                        </strong>{" "}
                        to <strong>31 December {year}</strong>, <br /> in accordance with the
                        Survey Act (Cap 299, Laws of Kenya) and its regulations.
                    </p>
                </div>

                {/* Signatures */}
                <div className="flex justify-between items-end pt-12">
                    <div className="text-center">
                        <img src="/signature.png" alt="signature" className="w-20 mx-auto" />

                        <small className="block border-t border-dashed mt-2">
                            Secretary to the Board
                        </small>
                    </div>

                    <div className="text-center">
                        <p className="font-medium mb-2">
                            {new Date(approvedDate).toLocaleDateString("en-KE")}
                        </p>

                        <small className="block border-t border-dashed">
                            Date of Issue
                        </small>
                    </div>
                </div>

                {/* QR */}
                <div className="flex flex-col items-center mt-6">
                    <QRCode value={VERIFY_URL} size={80} />
                    <small className="mt-2 font-medium">Scan to verify</small>

                    <small className="mt-1 break-all text-xs">
                        {signature}
                    </small>
                </div>

                {/* Footer */}
                <div className="mt-8 text-center border-t border-dashed">
                    <span>This certificate is system generated and verifiable via QR code.</span>

                    <div className="flex justify-center gap-4">
                        <span className="font-bold">Email: <b className="text-blue-600">info@lsb.go.ke</b></span>
                        <span className="font-bold">Website: <b className="text-blue-600">www.lsb.go.ke</b></span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnnualPracticingCertificate;